import { useEffect, useRef, useState } from "react";
import { Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DropdownSelect } from "components/DropdownSelect";
import { ContainerHeader } from "components/Ibox/style";
import { PrimaryButton } from "components/Button/style";
import { FaFileExcel, FaPlus, FaTruck } from "react-icons/fa";
import { MaisFiltros } from "components/ButtonModal";
import { CustomDatePicker } from "components/DatePicker";
import {
  Button,
  ButtonsContainer,
  Container,
  DatePickerContainer,
  FormContainer,
  InputContainer,
  InputContainerMaisFiltros,
  MaisFiltrosContainer,
  Title,
} from "./style";
import { FiltrosLote } from "../../interfaces/ILoteEstampagem";
import { useEmpresas } from "hooks/useEmpresas";
import { Spinner } from "react-bootstrap";

export const STATUS_EXCETO_FINALIZADO = "EXCETO_FINALIZADO";

const statusOptions = [
  { value: STATUS_EXCETO_FINALIZADO, label: "Todos (exceto Finalizado)" },
  { value: "", label: "Todos" },
  { value: "ABERTO", label: "Aberto" },
  { value: "FECHADO", label: "Fechado" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "ENTREGUE", label: "Entregue" },
  { value: "FINALIZADO", label: "Finalizado" },
];

interface IFiltrosForm {
  status: string;
  idEmpresa: string | number;
}

interface IFiltrosProps {
  onFiltrar: (filtros: FiltrosLote & { statusDisplay?: string }) => void;
  onNovoBotao: () => void;
  totalSelecionados?: number;
  loadingExportar?: boolean;
  onExportarSelecionados?: () => void;
  loadingVerificarTodos?: boolean;
  onVerificarTodosEnviados?: () => void;
}

export function Filtros({
  onFiltrar,
  onNovoBotao,
  totalSelecionados = 0,
  loadingExportar = false,
  onExportarSelecionados,
  loadingVerificarTodos = false,
  onVerificarTodosEnviados,
}: IFiltrosProps) {
  const { empresasOptions } = useEmpresas();
  const empresasComTodos = [{ value: "", label: "Todos" }, ...empresasOptions];

  const { control, watch, reset } = useForm<IFiltrosForm>({
    defaultValues: { status: STATUS_EXCETO_FINALIZADO, idEmpresa: "" },
  });

  const [dataInicio, setDataInicio] = useState<Date | undefined>(undefined);
  const [dataFim, setDataFim] = useState<Date | undefined>(undefined);
  const [dataInicioTemp, setDataInicioTemp] = useState<Date | undefined>(undefined);
  const [dataFimTemp, setDataFimTemp] = useState<Date | undefined>(undefined);

  const isFirstRender = useRef(true);
  const statusValue = watch("status");
  const idEmpresaValue = watch("idEmpresa");

  function dispararFiltro(datas?: { dataInicio?: Date; dataFim?: Date }) {
    const statusParam = statusValue === STATUS_EXCETO_FINALIZADO ? "" : statusValue;
    const di = datas?.dataInicio ?? dataInicio;
    const df = datas?.dataFim ?? dataFim;
    onFiltrar({
      status: statusParam as any,
      idEmpresa: idEmpresaValue as any,
      statusDisplay: statusValue,
      dataInicio: di ? di.toISOString().split("T")[0] : undefined,
      dataFim: df ? df.toISOString().split("T")[0] : undefined,
    });
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      dispararFiltro();
      return;
    }
    dispararFiltro();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusValue, idEmpresaValue]);

  function handleAplicarDatas() {
    setDataInicio(dataInicioTemp);
    setDataFim(dataFimTemp);
    dispararFiltro({ dataInicio: dataInicioTemp, dataFim: dataFimTemp });
  }

  function handleLimparDatas() {
    setDataInicioTemp(undefined);
    setDataFimTemp(undefined);
  }

  function handleCancelarDatas() {
    setDataInicioTemp(dataInicio);
    setDataFimTemp(dataFim);
  }

  function handleLimpar() {
    reset({ status: STATUS_EXCETO_FINALIZADO, idEmpresa: "" });
    setDataInicio(undefined);
    setDataFim(undefined);
    setDataInicioTemp(undefined);
    setDataFimTemp(undefined);
  }

  const temDatas = !!(dataInicio || dataFim);

  return (
    <Container>
      <ContainerHeader>
        <Title>Filtros</Title>
        <div className="d-flex gap-2 align-items-center" style={{ width: "auto" }}>
          {onVerificarTodosEnviados && (
            <PrimaryButton
              outline
              variante="primary"
              buttonWidth="xl"
              buttonWidthTipo="px"
              disabled={loadingVerificarTodos}
              onClick={onVerificarTodosEnviados}
            >
              {loadingVerificarTodos ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaTruck style={{ fontSize: "12px", marginBottom: "3px", marginRight: "6px" }} />
                  Verificar Entregas
                </>
              )}
            </PrimaryButton>
          )}
          {onExportarSelecionados && (
            <PrimaryButton
              outline
              variante="primary"
              buttonWidth="xl"
              buttonWidthTipo="px"
              disabled={totalSelecionados === 0 || loadingExportar}
              onClick={onExportarSelecionados}
            >
              {loadingExportar ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaFileExcel style={{ fontSize: "12px", marginBottom: "3px", marginRight: "6px" }} />
                  {totalSelecionados > 0 ? `Exportar Excel (${totalSelecionados})` : "Exportar Excel"}
                </>
              )}
            </PrimaryButton>
          )}
          <PrimaryButton buttonWidth="xl" buttonWidthTipo="px" onClick={onNovoBotao}>
            Novo Lote
            <FaPlus style={{ fontSize: "12px", marginBottom: "3px", marginLeft: "6px" }} />
          </PrimaryButton>
        </div>
      </ContainerHeader>
      <hr />
      <FormContainer>
        <Form noValidate>
          <Row>
            <InputContainer>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect options={statusOptions} title="" selectedValue={value} onSelect={onChange} />
                  )}
                />
              </Form.Group>
            </InputContainer>
            <InputContainer>
              <Form.Group>
                <Form.Label>Empresa</Form.Label>
                <Controller
                  control={control}
                  name="idEmpresa"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      options={empresasComTodos}
                      title=""
                      searchable
                      selectedValue={value ?? ""}
                      onSelect={(v) => onChange(v === "" ? "" : Number(v))}
                    />
                  )}
                />
              </Form.Group>
            </InputContainer>
            <InputContainer>
              <Form.Group>
                <ButtonsContainer>
                  <Button type="reset" outline variante="primary" onClick={handleLimpar}>
                    Limpar
                  </Button>
                </ButtonsContainer>
              </Form.Group>
            </InputContainer>
            <InputContainer>
              <MaisFiltrosContainer>
                <MaisFiltros
                  limparTudoOnClick={handleLimparDatas}
                  cancelarOnClick={handleCancelarDatas}
                  widthModal="md"
                  handleClickAplicarFiltros={handleAplicarDatas}
                >
                  <InputContainerMaisFiltros>
                    <Form.Group>
                      <DatePickerContainer>
                        <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>
                          Período de criação
                          {temDatas && (
                            <span style={{ fontSize: "0.75rem", color: "#0d6efd", marginLeft: 6 }}>● ativo</span>
                          )}
                        </span>
                        <CustomDatePicker
                          selected={dataInicioTemp ?? null}
                          onChange={(dates: any) => {
                            const [start, end] = Array.isArray(dates) ? dates : [dates, null];
                            setDataInicioTemp(start ?? undefined);
                            setDataFimTemp(end ?? undefined);
                          }}
                          startDate={dataInicioTemp}
                          endDate={dataFimTemp}
                          rangeSelect
                          placeholder="Selecione o período"
                        />
                      </DatePickerContainer>
                    </Form.Group>
                  </InputContainerMaisFiltros>
                </MaisFiltros>
              </MaisFiltrosContainer>
            </InputContainer>
          </Row>
        </Form>
      </FormContainer>
    </Container>
  );
}
